import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import accountsFlowApi from "../../services/accountsFlowService";
import AccountsFlowConstants from "../../common/AccountsFlowConstants";
import CustomerConstants from "../../common/CustomerConstants";
import { formatINR } from "../accounts/accountUser";
import PageShell from "../accounts/PageShell";
import CollapsibleSection from "../../Components/CollapsibleSection/CollapsibleSection";
import useDocNav, { DocLink } from "./useDocNav";
import { OrderCreditControl } from "../../Components/AccountsFlow/OrderCredit";
import "../accounts/accounts.css";
import "./flow.css";

/* Order details: the order, its loading advices, invoices and receipts, with each document's status. */

const TONE = { Paid: "ok", "Part Paid": "info", "Receipt Pending": "warn", "Receipt Rejected": "bad", Cancelled: "bad", APPROVED: "ok", PENDING: "warn", REJECTED: "bad" };
const Chip = ({ text }) => <span className={`accounts-chip ${TONE[text] || "muted"}`}>{text || "—"}</span>;

function when(value) {
  if (!value) return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function FlowOrderTrail() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const orderNo = params.get("no") || "";
  const nav = useDocNav();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [reload, setReload] = useState(0);

  useEffect(() => {
    setData(null);
    setError("");
    if (!orderNo) return;
    accountsFlowApi.orderTrail(orderNo).then((result) => setData(result.data)).catch((err) => setError(err.message));
  }, [orderNo, reload]);

  if (!data) {
    return <PageShell title={`Order ${orderNo}`}><div className={error ? "accounts-error" : ""}>{error || "Loading..."}</div></PageShell>;
  }
  const { order, loadingAdvices, invoices, receipts, totals } = data;

  return (
    <PageShell
      title={`Order ${order.orderNo}`}
      subtitle={`${order.customerName}${order.customerCode ? ` · ${order.customerCode}` : ""}`}
      actions={(
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(CustomerConstants.CUSTOMER_V2_ORDER_BOOKING_LIST_LINE_ITEM)}><i className="bi bi-arrow-left" /> Order Booking</button>
          <button type="button" className="btn btn-outline-primary" onClick={() => navigate(CustomerConstants.CUSTOMER_V2_INVOICE_LIST)}><i className="bi bi-receipt" /> Invoices</button>
        </>
      )}
    >
      <div className="accounts-entry">
        <CollapsibleSection title="Order" meta={order.date ? when(order.date) : ""}>
          <div className="accounts-panel">
            <p><Chip text={order.status} /></p>
            <div className="accounts-facts">
              <div><span className="accounts-hint">Customer</span><strong>{order.customerName}</strong></div>
              <div><span className="accounts-hint">Company</span><strong>{order.company || "—"}</strong></div>
              <div><span className="accounts-hint">Sales officer</span><strong>{order.salesOfficer || "—"}</strong></div>
              <div><span className="accounts-hint">Order value</span><strong>{formatINR(order.total)}</strong></div>
              <div><span className="accounts-hint">Invoiced</span><strong>{formatINR(totals.invoiced)}</strong></div>
              <div><span className="accounts-hint">Received</span><strong>{formatINR(totals.received)}</strong></div>
              <div><span className="accounts-hint">Pending approval</span><strong>{formatINR(totals.pending)}</strong></div>
              <div><span className="accounts-hint">Open balance</span><strong className={totals.open > 0 ? "tally-error" : ""}>{formatINR(totals.open)}</strong></div>
            </div>
          </div>
        </CollapsibleSection>
        {order.credit && (
          <CollapsibleSection title="Credit approval" meta={order.credit.creditStatus === "CREDIT_HOLD" ? "Loading Advice waits for approval" : ""}>
            <div className="accounts-panel">
              <OrderCreditControl order={{ ...order.credit, orderNo: order.orderNo }} onDone={() => setReload((n) => n + 1)} />
              {order.credit.creditCheck && (
                <div className="accounts-facts" style={{ marginTop: 12 }}>
                  <div><span className="accounts-hint">Credit limit</span><strong>{formatINR(order.credit.creditCheck.creditLimit)}</strong></div>
                  <div><span className="accounts-hint">Outstanding when booked</span><strong>{formatINR(order.credit.creditCheck.outstanding)}</strong></div>
                  <div><span className="accounts-hint">Pending orders when booked</span><strong>{formatINR(order.credit.creditCheck.pendingOrders)}</strong></div>
                  <div><span className="accounts-hint">Available when booked</span><strong>{formatINR(order.credit.creditCheck.available)}</strong></div>
                  <div><span className="accounts-hint">Order value</span><strong>{formatINR(order.credit.creditCheck.orderValue)}</strong></div>
                  <div><span className="accounts-hint">Over the limit by</span><strong className="tally-error">{formatINR(order.credit.creditCheck.exceededBy)}</strong></div>
                </div>
              )}
              {order.credit.approvedBy && <p className="accounts-hint" style={{ marginTop: 8 }}>Approved by {order.credit.approvedBy} on {when(order.credit.approvedAt)}: {order.credit.approvalNote}</p>}
              {order.credit.rejectedBy && <p className="accounts-hint" style={{ marginTop: 8 }}>Rejected by {order.credit.rejectedBy} on {when(order.credit.rejectedAt)}: {order.credit.rejectReason}</p>}
            </div>
          </CollapsibleSection>
        )}
        <CollapsibleSection title="Status history">
          <div className="accounts-panel">
            {order.history.length === 0 && <p className="accounts-hint">No status events recorded yet.</p>}
            <ol className="flow-timeline">
              {order.history.map((event, index) => (
                <li key={index}>
                  <Chip text={event.status} />
                  {event.note ? <span className="accounts-hint"> {event.note}</span> : null}
                  <div className="accounts-hint">{when(event.at)}</div>
                </li>
              ))}
            </ol>
          </div>
        </CollapsibleSection>
      </div>

      <CollapsibleSection title="Order lines">
        <div className="accounts-table-wrap">
          <table className="accounts-table">
            <thead><tr><th>Material</th><th className="num">Quantity</th><th className="num">Unit price</th><th className="num">Net value</th></tr></thead>
            <tbody>
              {order.items.map((item, index) => (
                <tr key={index}>
                  <td>{item.material}<div className="accounts-hint">{item.materialCode}</div></td>
                  <td className="num">{item.quantity} {item.unit}</td>
                  <td className="num">{formatINR(item.unitPrice)}</td>
                  <td className="num">{formatINR(item.netValue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Loading advices" meta={`${loadingAdvices.length}`}>
        <div className="accounts-table-wrap">
          <table className="accounts-table">
            <thead><tr><th>Loading advice</th><th>Date</th><th>Truck</th><th className="num">Quantity</th><th>Invoice</th><th>Status</th></tr></thead>
            <tbody>
              {loadingAdvices.map((row) => (
                <tr key={row.loadingAdviceNr}>
                  <td><DocLink onClick={() => nav.openLoadingAdvice(row.loadingAdviceNr)}>{row.loadingAdviceNr}</DocLink></td>
                  <td>{when(row.date)}</td>
                  <td>{row.truck || "—"}</td>
                  <td className="num">{row.quantity}</td>
                  <td><DocLink onClick={() => nav.openInvoice(row.invoiceNr)}>{row.invoiceNr}</DocLink></td>
                  <td><Chip text={row.status} /></td>
                </tr>
              ))}
              {loadingAdvices.length === 0 && <tr><td colSpan={6}>No loading advice yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Invoices" meta={`${invoices.length}`}>
        <div className="accounts-table-wrap">
          <table className="accounts-table">
            <thead><tr><th>Invoice</th><th>Date</th><th className="num">Amount</th><th className="num">Received</th><th className="num">Pending</th><th className="num">Open</th><th>Sales voucher</th><th>Status</th></tr></thead>
            <tbody>
              {invoices.map((row) => (
                <tr key={row.invoiceNr}>
                  <td><DocLink onClick={() => nav.openInvoice(row.invoiceNr)}>{row.invoiceNr}</DocLink><div className="accounts-hint">{row.loadingAdviceNr}</div></td>
                  <td>{when(row.date)}</td>
                  <td className="num">{formatINR(row.amount)}</td>
                  <td className="num">{formatINR(row.approved)}</td>
                  <td className="num">{formatINR(row.pending)}</td>
                  <td className="num">{formatINR(row.remaining)}</td>
                  <td>{row.salesVoucherNo || "—"}</td>
                  <td><Chip text={row.status} /></td>
                </tr>
              ))}
              {invoices.length === 0 && <tr><td colSpan={8}>No invoice yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Receipts" meta={`${receipts.length}`}>
        <div className="accounts-table-wrap">
          <table className="accounts-table">
            <thead><tr><th>Receipt</th><th>Date</th><th>Invoice</th><th>Mode</th><th className="num">Amount</th><th>Entered by</th><th>Status</th></tr></thead>
            <tbody>
              {receipts.map((row) => (
                <tr key={row.id}>
                  <td>
                    <DocLink onClick={() => navigate(`${AccountsFlowConstants.ENTRY_VIEW}?id=${row.id}`)}>{row.voucherNo || row.requestNo}</DocLink>
                    {row.voucherNo ? <div className="accounts-hint">{row.requestNo}</div> : null}
                  </td>
                  <td>{row.date}</td>
                  <td>{row.invoiceNr || "—"}</td>
                  <td>{row.mode}</td>
                  <td className="num">{formatINR(row.amount)}</td>
                  <td>{row.enteredBy}{row.approvedBy ? <div className="accounts-hint">approved by {row.approvedBy}</div> : null}</td>
                  <td><Chip text={row.statusLabel} /></td>
                </tr>
              ))}
              {receipts.length === 0 && <tr><td colSpan={7}>No receipts yet.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="accounts-form-actions start">
          <button type="button" className="btn btn-link" onClick={() => navigate(AccountsFlowConstants.VOUCHERS)}>Voucher list</button>
        </div>
      </CollapsibleSection>
    </PageShell>
  );
}
